<template>

   <div class="row mt-2 mb-2 text-center">
      <h6 class="card-subtitle text-muted">Contract Version: <strong style="color:orange">{{ contract.contract_version
      }}</strong></h6>
   </div>

   <form ref="editBlockFormRef" class="row g-0">
      <LoadingPage v-if="isLoading"></LoadingPage>

      <div>
         <label for="organisation">
            Organisation
            <span v-if="contract.original_organisation_name && contract.organisation_name !== contract.original_organisation_name" class="badge bg-info text-white ms-1" style="font-size: 10px;" :title="'Original Lead Name: ' + contract.original_organisation_name">
               <i class="fa-solid fa-history me-1"></i>Formerly: {{ contract.original_organisation_name }}
            </span>
         </label>
         <input type="text" id="organisation" v-model="contract.organisation_name" disabled class="form-control"
            placeholder="Organisation" />
      </div>

      <div>
         <label for="organisation_abn">
            Organisation ABN
            <span v-if="contract.original_abn && contract.abn !== contract.original_abn" class="badge bg-info text-white ms-1" style="font-size: 10px;" :title="'Original Lead ABN: ' + contract.original_abn">
               <i class="fa-solid fa-history me-1"></i>Formerly: {{ contract.original_abn }}
            </span>
         </label>
         <input type="text" id="organisation_abn" v-model="contract.abn" disabled class="form-control"
            :placeholder="(contract.abn) ? contract.abn : 'No ABN'" />
      </div>

      <div>
         <label for="contract_id">Linked Contract ID</label>
         <input type="text" id="contract_id" v-model="contract.contract_id" disabled class="form-control"
            :placeholder="(contract.contract_id) ? contract.contract_id : 'No Contract ID'" />
      </div>

      <div>
         <label for="contract_owner">Contract Owner</label>
         <input type="text" id="contract_owner" v-model="contract.contract_owner" disabled class="form-control"
            placeholder="Contract Owner" />
      </div>

      <div>
         <label for="client_lead_name">Client Lead Name</label>
         <input type="text" id="client_lead_name" v-model="contract.client_lead_name" disabled class="form-control"
            placeholder="Lead Name" />
      </div>

      <div>
         <label for="client_lead_email">Client Lead Email</label>
         <input type="text" id="client_lead_email" v-model="contract.client_lead_email" disabled class="form-control"
            placeholder="Lead Email" />
      </div>

      <div>
         <label for="value_quoted">Total Amount</label>
         <input type="text" id="value_quoted" v-model="contract.total_amount" disabled class="form-control"
            placeholder="Value Quoted" />
      </div>

      <div>
         <label for="value_accepted">Accepted Amount</label>
         <input type="text" id="value_accepted" v-model="contract.total_accepted" disabled class="form-control"
            placeholder="Value Accepted" />
      </div>

      <div class="status-container" style="margin-bottom: 10px;">
         <div class="status-item">
            <label>Approval Status</label>
            <div v-if="contract.is_approved" class="d-flex align-items-center gap-2 mt-1">
               <span class="spark-pill-badge spark-pill-badge--approved"><i class="fa-solid fa-check"></i> Approved</span>
            </div>
            <div v-else-if="contract.approval_requested_to > 0" class="d-flex align-items-center gap-2 mt-1">
               <span class="spark-pill-badge spark-pill-badge--pending-approval"><i class="fa-solid fa-clock-rotate-left"></i> Pending Approval</span>
            </div>
            <div v-else class="d-flex align-items-center gap-2 mt-1">
               <span class="spark-pill-badge spark-pill-badge--not-requested"><i class="fa-solid fa-circle-minus"></i> Not Requested</span>
            </div>
         </div>
         <div class="status-item">
            <label>Requested Approver</label>
            <input type="text" :value="contract.approval_requested_to > 0 ? (contract.approval_requested_to_email || getVertexAuthUser(contract.approval_requested_to)) : 'None'" disabled class="form-control" />
         </div>
         <div class="status-item">
            <label>Approved By</label>
            <input type="text" :value="contract.is_approved ? getVertexAuthUser(contract.approved_by) : 'Not yet approved'" disabled class="form-control" />
         </div>
         <div class="status-item">
            <label>Approved Date</label>
            <input type="text" :value="contract.is_approved ? formatDate(contract.approved_at) : 'Not yet approved'" disabled class="form-control" />
         </div>
      </div>

      <div>
         <label class="checkbox-label" @click.stop>
              <input type="checkbox" v-model="contract.show_discount_calculation" class="styled-checkbox" :disabled="!canEditContract">
              <span class="checkbox-custom"></span>
              Show Discount Calculation on Contract Landing Page?
            </label>
      </div>
      
      <div class="status-container">
         <div class="status-item">
            <label for="date_created">Date Created</label>
            <input type="text" id="date_created" v-model="contract.created_at" disabled class="form-control"
               value="20-Sep-2025" />
         </div>
         <div class="status-item">
            <label for="date_sent">Date Sent</label>
            <input type="text" id="date_sent" v-model="contract.sent_at" disabled class="form-control"
               :placeholder="(contract.sent_at) ? contract.sent_at : 'Not yet sent'" />
         </div>

         <div class="status-item full-width">
            <label>Shared With:</label>
            <div v-if="contract.shared_users && contract.shared_users.length > 0" class="shared-users-list">
               <div v-for="share in contract.shared_users" :key="share.email" class="shared-user-item-inline">
                  <span class="shared-user-email">{{ share.email }}</span>
                  <span class="share-type-badge">{{ share.share_type }}</span>
               </div>
            </div>
            <div v-else>
               <p class="text-muted" style="margin-top: 5px;">Not shared with anyone yet.</p>
            </div>
         </div>
         <div class="status-item full-width">
            <label for="d_t_accepted">Date and Time Accepted</label>
            <input type="text" id="d_t_accepted" v-model="contract.accepted_at" disabled class="form-control"
               :placeholder="(contract.accepted_at) ? contract.accepted_at : 'Not yet accepted'" />
         </div>

         <div class="status-item full-width">
            <label for="d_t_accepted">Expiry Date</label>
            <input type="text" id="d_t_accepted" v-model="contract.expiry_date" disabled class="form-control"
               :placeholder="(contract.expiry_date == null && DaysToAcceptCounter(contract.expiry_date) < -1000) && contract.sent_at != null ? 'Not yet sent' : contract.expiry_date" />
         </div>

         <div class="acceptance-info">
            <div class="acceptance-item">
               <label for="accepted_by_email">Accepted by Email</label>
               <div v-if="this.contract.accepted_by_email == null" class="acceptance-value no">Not yet accepted</div>
               <div v-else class="acceptance-value yes"> {{ this.contract.accepted_by_email }}</div>
            </div>
            <div class="acceptance-item">
               <label for="accepted_by_ip">Accepted By IP Address</label>
               <div v-if="this.contract.accepted_by_ip == null" class="acceptance-value no">Not yet accepted</div>
               <div v-else class="acceptance-value yes">{{ this.contract.accepted_by_ip }}</div>
            </div>
         </div>
      </div>


   </form>

</template>

<script>
import moment from "moment";
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
//  import { isEmpty } from '@/plugins/helper.js'
//  import 'vue-tel-input/dist/vue-tel-input.css'
//  import VueMultiselect from 'vue-multiselect'
//  import 'vue-multiselect/dist/vue-multiselect.css'
//  import "@/assets/scss/vue-multiselect.scss";
import LoadingPage from '@/components/Utilities/vertex-loading.vue'
import { nextTick } from 'vue'; // Import nextTick
import { debounce, formatCurrency, log, isEmpty } from '@/plugins/helper.js'

//const _ = require('lodash');

export default {

   components: {
      //    VueMultiselect,
      LoadingPage,
   },

   setup() {
      return { v$: useVuelidate() }
   },

   emits: ['onSubmit', 'onDelete', 'onInitializeDetails'],

   props: {
      item: {
         type: Object,
         // required: true,
      },
      id: {
         type: String,
      },
   },

   data() {
      return {
         currentDate: null,
         contract: {
            created_date: null,
            organisation_name: "",
            abn: "",
            original_organisation_name: "",
            original_abn: "",
            contract_id: null,
            contract_owner: null,
            contract_title: "",
            client_lead_name: "",
            client_lead_email: "",
            total_amount: 0.00,
            total_accepted: 0.00,
            contract_version: "",
            created_at: null,
            expiry_date: null,
            sent_at: null,
            accepted_at: null,
            content: "",
            contract_items: [],
            contract_status: 0,
            shared_users: [],
            accepted_by_email: null,
            accepted_by_ip: null,
            option_index_selected: null,
            is_approved: false,
            approved_by: null,
            approved_at: null,
            approval_requested_by: 0,
            approval_requested_to: 0,
            approval_requested_to_email: '',
            has_revision_not_sent: false,
         },
         productList: [],
         isEditing: false,
         disabledWhenClicked: false,
         isLoading: false,
         debounceFn: null,
         currentBlockType: "none",
         currentContractName: "none",
         lead: null,
         ShowDiscountChanged: false,
         ShowDiscountCurrentValue: false,
         vtxUsers: [],
      }
   },

   validations() {
      return {
         block: {
            blockName: { required },
            blockType: { required },
            blockUse: { required },
         },
      }
   },

    computed: {
      canEditContract() {
         // Lock editing if the contract is locked (pending approval, approved, or executed)
         if (this.contract.contract_status === 4 || this.contract.contract_status === 6 || this.contract.contract_status === 7) {
            return false;
         }
         // Global Super Admin can edit any contract
         if (this.$store.getters['auth/isGlobalSuperAdmin']) {
            return true;
         }
         // Spark Consultant can edit if they own the lead/contract
         if (this.$store.state.auth.isConsultant) {
            const currentUserId = this.$store.state.auth?.currentUser?.id || 0;
            const creatorId = this.lead ? this.lead.created_by : 0;
            return creatorId > 0 && currentUserId === creatorId;
         }
         return false;
      },
    },

   watch: {
      currentBlockType() {
         if (this.currentContractName !== "none") {
            this.setHeaderTitle()
         }
      }
   },

   created() {
      this.init()
      this.debounceFn = debounce(() =>
         this.setContentIfNotEmpty()
         , 2500)
      this.setHeaderTitle()
   },

   mounted() {
      nextTick(() => {
         this.$parent.setContent(this.contract.content, false)
      })
   },

   methods: {
      init() {
         this.getVertexUsers()
         this.currentDate = moment(new Date()).format("DD-MMM-YYYY");
      },


      formatDate(date) {
         if (!date || !moment(date).isValid() || moment(date).year() < 1980) {
            return null;
         }
         return moment(date).format("DD-MMM-YYYY @ HH:mm:ss");
      },

      formatDateNoTime(date) {
         if (!date || !moment(date).isValid() || moment(date).year() < 1980) {
            return null;
         }
         return moment(date).format("DD-MMM-YYYY");
      },

      formatUtcToTimeZone(utcDateString) {
         // 1. Parse the UTC date string into a Date object.
         // JavaScript Date objects internally represent time as milliseconds since the Unix epoch,
         // which is timezone-agnostic (effectively UTC).
         const date = new Date(utcDateString);

         // 2. Define formatting options.
         // 'timeZone' option tells Intl.DateTimeFormat to convert the date to this specific time zone.
         const options = {
            day: '2-digit',   // DD (e.g., 01, 15, 31)
            month: 'short',  // MMM (e.g., Jan, Feb, Dec)
            year: 'numeric',  // YYYY (e.g., 2023, 2025)
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            // Optional: timeZoneName: 'short' or 'long' if you want to include the time zone name
         };

         // 3. Create an Intl.DateTimeFormat instance and format the date.
         return new Intl.DateTimeFormat('en-US', options).format(date);
      },

      formatToTimestamp(date) {
         const day = date.getDate();
         const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
         const month = monthNames[date.getMonth()];
         const year = date.getFullYear();
         const hours = date.getHours().toString().padStart(2, '0');
         const minutes = date.getMinutes().toString().padStart(2, '0');
         const seconds = date.getSeconds().toString().padStart(2, '0');
         const formattedDate = `${day}-${month}-${year} @ ${hours}:${minutes}:${seconds}`;

         if (year < 1980) {
            return null;
         }

         return formattedDate;
      },

      /**
* Calculates the number of calendar days between the current date (now) and an expiry date.
* The calculation considers full calendar days, meaning it normalizes both dates to the
* start of their respective days in the user's local timezone.
*
* @param {string} utcDateString The expiry date string in UTC format (e.g., "2025-06-08T05:00:00Z").
* @returns {number} The difference in full calendar days.
* Positive if the expiry date is in the future, negative if in the past.
* 0 if it's the same calendar day.
*/
      DaysToAcceptCounter(utcDateString) {
         // 1. Get the expiry date as a Date object.
         //    It's parsed from a UTC string, but the Date object itself stores a universal timestamp.
         const expiryDate = new Date(utcDateString);

         // 2. Get the current date and time in the user's local timezone.
         const now = new Date();

         // 3. To accurately count "calendar days" (days on the calendar, ignoring time of day),
         //    we need to determine the start of the day for both 'now' and 'expiryDate'
         //    in the *user's local timezone*.

         //    Create a new Date object representing the start of the current day (00:00:00)
         //    in the user's local timezone. By passing year, month, and day to the Date constructor,
         //    it automatically creates a date in the local timezone at midnight.
         const startOfTodayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());

         //    Create a new Date object representing the start of the expiry day (00:00:00)
         //    in the user's local timezone.
         //    We extract the year, month, and date components from the 'expiryDate' using its local getters
         //    (e.g., `getFullYear()`, `getMonth()`, `getDate()`). These getters automatically
         //    convert the internal UTC timestamp to the corresponding local calendar day.
         //    Then, creating a new Date object with these local components ensures we get the
         //    midnight of that specific local calendar day.
         const startOfExpiryDayLocal = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());

         // 4. Calculate the difference in milliseconds between these two "start-of-day" timestamps.
         const differenceInMs = startOfExpiryDayLocal.getTime() - startOfTodayLocal.getTime();

         // 5. Convert the millisecond difference to days.
         //    We divide by the number of milliseconds in a standard day (24 hours).
         const millisecondsPerDay = 1000 * 60 * 60 * 24;
         let daysDifference = differenceInMs / millisecondsPerDay;

         // 6. Round the result to the nearest whole number.
         //    Using Math.round() helps in handling any minor floating-point inaccuracies or
         //    cases where a "day" might be slightly more or less than 24 hours due to
         //    Daylight Saving Time (DST) transitions. This ensures we get clear whole calendar day counts.
         return Math.round(daysDifference);
      },

      editContract(id) {
         this.getContract(id)
      },

      triggerDebounce() {
         this.debounceFn()
      },

      async setContentIfNotEmpty() {
         this.isLoading = true
         setTimeout(() => {
            this.isLoading = false 
            //this.toggleIsLoading()
            if (this.contract.content != undefined && this.contract.content != null) {
               this.$parent.setContent(this.contract.content, false)
            }
         }, 2500)
      },

      async setHeaderTitle() {
         this.$parent.setHeaderTitle(this.currentContractName)
      },

      toggleIsLoading() {
         this.isLoading = !this.isLoading
      },

      getInternalAccessKey() {
         return this.contract.internal_key
      },

      getUniqueKey() {
         return this.contract.unique_key
      },

      async getContract(id) {
         this.isLoading = true;

         await this.$axios.get("/contracts/" + id)
            .then((response) => {
               let resp = response.data.vertex_contract
               this.contract.id = resp.id
               this.contract.unique_key = resp.unique_key
               this.contract.internal_key = resp.internal_key
               this.contract.content = resp.content
               this.contract.created_date = resp.created_at
               let acceptedOrgName = ""
               let acceptedAbn = ""
               if (resp.contract_acceptance) {
                  acceptedOrgName = resp.contract_acceptance.organisation_name
                  acceptedAbn = resp.contract_acceptance.abn
               }
               let leadOrgName = resp.Lead ? (resp.Lead.auth_organisation_name || resp.Lead.organisation_name) : ""
               let leadAbn = resp.Lead ? resp.Lead.abn : ""
                let originalLeadOrgName = resp.Lead ? (resp.Lead.original_organisation_name || "") : ""
                let originalLeadAbn = resp.Lead ? (resp.Lead.original_abn || "") : ""

               this.contract.organisation_name = acceptedOrgName || leadOrgName
               this.contract.abn = acceptedAbn || leadAbn
               this.contract.original_organisation_name = originalLeadOrgName
               this.contract.original_abn = originalLeadAbn
               this.contract.contract_id = resp.contract_id
               this.contract.contract_owner = this.getVertexAuthUser(resp.created_by)
               this.contract.contract_title = resp.contract_title
               this.contract.client_lead_name = resp.Lead.first_name + " " + resp.Lead.last_name
               this.contract.client_lead_email = resp.Lead.email
               this.contract.total_amount = formatCurrency(resp.contract_total_amount)
               this.contract.total_accepted = formatCurrency(resp.contract_accepted_amount)
               this.contract.expiry_date = (resp.expiry_date != null) ? this.formatDateNoTime(resp.expiry_date) : null
               this.contract.contract_version = resp.contract_version
               this.contract.created_at = this.formatDate(resp.created_at)
               this.contract.sent_at = (resp.sent_at != null) ? this.formatDate(resp.sent_at) : null
               this.contract.accepted_at = (resp.accepted_at != null) ? this.formatDate(resp.accepted_at) : null
               this.contract.contract_status = (resp.contract_status != null && resp.contract_status != undefined) ? resp.contract_status : null 
               this.contract.show_discount_calculation = resp.show_discount_calculation
               this.contract.contract_section_content = resp.contract_section_content
               this.ShowDiscountCurrentValue = resp.show_discount_calculation
               this.lead = resp.Lead
               this.currentContractName = this.contract.contract_title
               this.setHeaderTitle()
               this.contract.contract_items = resp.contract_items
               this.contract.shared_users = resp.shared_users || []
               this.contract.accepted_by_email = resp.accepted_by_email || null
               this.contract.accepted_by_ip = resp.accepted_by_ip || null
               this.contract.option_index_selected = resp.option_index_selected || null
               this.contract.is_approved = resp.is_approved || false
               this.contract.approved_by = resp.approved_by || null
               this.contract.approved_at = resp.approved_at || null
               this.contract.approval_requested_by = resp.approval_requested_by || 0
               this.contract.approval_requested_to = resp.approval_requested_to || 0
               this.contract.approval_requested_to_email = resp.approval_requested_to_email || ''
               this.contract.has_revision_not_sent = resp.has_revision_not_sent || false
               this.$emit('onInitializeDetails')
               this.isLoading = false;
            }).catch((error) => {
               this.$toast.error(error)
               this.isLoading = false;
            })
      },

      getVersion() {
         return this.contract.contract_version
      },

      getContractItems() {

         if (!isEmpty(this.contract.contract_items)) {
            this.contract.contract_items.forEach(prop_item => {
               prop_item['is_charity'] = this.lead.is_charity
            })

            return this.contract.contract_items
         }

         return []

         // let pitemsarray = []
         // if(!isEmpty(this.contract.contract_items))
         // this.contract.contract_items.forEach(pitem =>{
         //    pitemsarray.push({
         //       description: pitem.description,
         //       discount: pitem.discount,
         //       id: pitem.id,
         //       price: pitem.price,
         //       product_id: pitem.product_id,
         //       contract_id: pitem.contract_id,
         //       quantity: pitem.quantity
         //    })
         // })

         // return pitemsarray
      },

      isShowDiscountChanged() {
         return this.ShowDiscountCurrentValue == this.contract.show_discount_calculation
      },


      async updateContract(contract_section_content) {

         log("contract_id", this.contract.id)

         contract_section_content["show_discount_calculation"] = this.contract.show_discount_calculation

         return await this.$axios.put("/contracts/" + this.contract.id + "/update-content", contract_section_content)
            .then((response) => {
               if (response.data.success) {
                  this.isLoading = true
                  this.editContract(this.contract.id)
                  this.isLoading = false
                  this.$parent.clearIsDirty()
                  //this.ShowDiscountChanged = false
                  this.$toast.success(response.data.message)
                  return true;
               } else {
                  this.isLoading = false
                  this.$toast.error(response.data.message)
                  return false;
               }
            })
            .catch((error) => {
               this.isLoading = false
               console.error(error)
               this.$toast.error(error.response?.data?.message || error.message || "Failed to update contract")
               return false;
            })
      },

      async sendContract() {

         log("contract_id", this.contract.id)

         await this.$axios.put("/contracts/" + this.contract.id + "/send")
            .then((response) => {
               if (response.data.success) {
                  this.isLoading = true
                  this.editContract(this.contract.id)
                  this.isLoading = false
                  this.$toast.success(response.data.message)
               } else {
                  this.isLoading = false
                  this.$toast.error(response.data.message)
               }
            })
            .catch((error) => {
               this.isLoading = false
               console.error(error)
               this.$toast.error(error.response.data.message)
            })
      },

      getVertexAuthUser(id) {
         // Static fallback user while auth is being migrated
         const staticUsers = [
            { id: 1, first_name: 'Richie', last_name: 'Golez' }
         ];

         let user = Array.isArray(this.vtxUsers) ? this.vtxUsers.find(user => user && user.id === id) : null;
         if (user) {
            return user.first_name + " " + user.last_name
         }

         // Check static users as fallback
         let staticUser = staticUsers.find(u => u && u.id === id);
         if (staticUser) {
            return staticUser.first_name + " " + staticUser.last_name
         }

         if (id != "") {
            return "Deleted User"
         } else {
            return "System Generated"
         }
      },

      getVertexUsers() {
          this.vtxUsers = this.$store.state.auth.usersWithVertexRoles || []
         // await this.$axios_auth.get("/supadmin/users")
         //    .then((response) => {
         //       sortAlphabetically(response.data.users, (item) => item.user.first_name)
         //       this.vtxUsers = response.data.users
         //       //this.$store.commit("auth/setUsersWithVertexRoles", response.data.users)
         //    })
         //    .catch((error) => {
         //       console.error(error)
         //       // this.$toast.error(error.response.data.message)
         //    })
      },

      resetForm() {
         this.contract = null
      },
   },

};

</script>

<style scoped>
.version-status-icon {
   display: inline-flex;
   justify-content: center;
   align-items: center;
   min-width: 45px;
   height: 24px;
   padding: 0 8px;
   border-radius: 6px;
   color: white;
   font-size: 12px;
   font-weight: bold;
   text-transform: none;
   white-space: nowrap;
   vertical-align: middle;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.high {
   color: #EB1C24;
}

.low {
   color: #00A54F;
}

.medium {
   color: #F3994D;
}

select>option {
   color: #435666;
   /* border: 1px solid #D9D9D9; */
   /* border-radius: 5px; */
}

.btn {
   width: 7rem;
}

form label:not(#isCharityLbl) {
   color: #00A0C1;
   margin: 0.5rem 0;
}

input:not([type="checkbox"]),
select {
   height: 40px;
   border-radius: 10px;
   border: 1px solid #D9D9D9;
   color: #435666;
}

.form--check-label {
   /* Adjust spacing between checkbox and label */
   color: #495057 !important;
   font-size: 15px;
   align-items: center;
   /* Vertically center the content */
   justify-content: center;
   /* Horizontally center the content */
   /* Grayish color for the label */
}

.form--check-input {
   width: 12px;
   /* Increase width */
   height: 12px;
   /* Increase height */
   margin-left: -17px;
   margin-right: 10px;
}

.add-space {
   margin-bottom: 225px;
}

.btn-circle-color {
   width: 40px;
   height: 40px;
   padding: 6px 0px;
   border-radius: 15px;
   text-align: center;
   font-size: 12px;
   line-height: 1.42857;
   border: 1px solid #00A0C1;
   color: #00A0C1;
}

.status-container {
   display: grid;
   grid-template-columns: auto auto;
   /* Creates two equal width columns */
   gap: 10px;
   /* Adds some space between the grid items */
   padding: 15px;
   background-color: #f7f7f7;
   /* Light background */
   border-radius: 8px;
   /* Rounded corners */
}

.status-item {
   display: flex;
   flex-direction: column;
   padding: 10px;
   background-color: white;
   /* Slightly darker background for items */
   border-radius: 5px;
}

.full-width {
   grid-column: 1 / -1;
   /* Make this item span both columns */
}

.status-label {
   font-size: 0.85em;
   color: #555;
   margin-bottom: 5px;
}

.status-value {
   font-weight: bold;
   color: #333;
}

.acceptance-info {
   grid-column: 1 / -1;
   /* Span both columns */
   display: flex;
   gap: 10px;
   margin-top: 10px;
}

.acceptance-item {
   display: flex;
   flex-direction: column;
   flex: 1;
   /* Distribute available space equally */
   padding: 10px;
   background-color: white;
   border-radius: 5px;
   text-align: center;
   /* Center the content */
}

.acceptance-label {
   font-size: 0.8em;
   color: #555;
   margin-bottom: 5px;
}

.acceptance-value {
   font-weight: bold;
}

.yes {
   color: green;
}

.no {
   color: orange;
}

.checkbox-label {
  display: inline-flex;
  /* Changed to inline-flex for better alignment in the cell */
  align-items: center;
  cursor: pointer;
  font-weight: 500;
  color: #333;
}

/* Hide the default browser checkbox */
.styled-checkbox {
  display: none;
}

/* The custom checkbox container */
.checkbox-custom {
  width: 20px;
  height: 20px;
  border: 2px solid #00bcd4;
  border-radius: 4px;
  margin-right: 12px;
  display: inline-block;
  position: relative;
  transition: background-color 0.2s, border-color 0.2s;
}

/* Create the checkmark/tick using a pseudo-element */
.checkbox-custom::after {
  content: '';
  position: absolute;
  display: none;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 3px 3px 0;
  transform: rotate(45deg);
}

/* Style the custom checkbox when checked */
.styled-checkbox:checked+.checkbox-custom {
  background-color: #00bcd4;
  border-color: #00bcd4;
}

/* Show the checkmark when the checkbox is checked */
.styled-checkbox:checked+.checkbox-custom::after {
  display: block;
}

/* START: UPDATED CSS FOR "SHARED WITH" SECTION */
.shared-users-list {
  margin-top: 5px;
}

.shared-user-item-inline {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.3rem 0;
    /* Add a border for separation if you have multiple users */
    border-bottom: 1px solid #f0f0f0;
}

.shared-user-item-inline:last-child {
   border-bottom: none; /* Remove border from the last item */
}

.shared-user-email {
    color: #435666;
    font-size: 0.9em;
}

.share-type-badge {
    background-color: #e9ecef;
    color: #495057;
    padding: 0.25rem 0.75rem;
    border-radius: 10px;
    font-size: 0.8em;
    font-weight: bold;
    text-transform: capitalize;
}


.form-control-static-container {
  min-height: 40px;
  padding: 0.6rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #435666;
  background-color: #e9ecef;
  border: 1px solid #ced4da;
  border-radius: 10px;
  opacity: 1;
}

.shared-users-list {
  margin-top: 0;
}

</style>
